import { useContext, useEffect, useMemo, useState } from 'react';

import aggregateMetricMatrix from 'api/aggregateMetricMatrix';
import GqlClientContext from 'contexts/GqlClientContext';
import useDateScope from 'hooks/useDateScope';
import useFilterInput from 'hooks/useFilterInput';
import useMetricAggregationRequest from 'hooks/useMetricAggregationRequest';
import useValueFormatters from 'hooks/useValueFormatters';

// Single aggregated value for a metric over the current DateInputContext range
// and the block's filters, or over the period it's compared against when a
// comparison is given (keep that
// object stable, it's a dependency of the date scope). Goes through
// useMetricAggregationRequest so compound metrics resolve their inputs (and we
// never fire before they're ready).
const useStudioMetricValue = ({
  metric,
  comparison,
}: {
  metric: Metrics.Metric | undefined;
  comparison?: PersistedComparisonType;
}) => {
  const { client } = useContext(GqlClientContext);
  const dateScope = useDateScope({ comparison });
  // The block's (and element's) filters, from its FilterScope
  const { filterInput } = useFilterInput({});
  const { formatMetric } = useValueFormatters();
  const { resolveMetricForRequest } = useMetricAggregationRequest();

  const resolved = useMemo(
    () => (metric ? resolveMetricForRequest(metric) : undefined),
    [metric, resolveMetricForRequest],
  );

  const [value, setValue] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (!metric || !resolved || !resolved.ready) {
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setIsError(false);

    aggregateMetricMatrix({
      client,
      metrics: [
        ...resolved.metricInputs,
        ...(resolved.special ? [resolved.special] : []),
      ],
      expressions: resolved.expression ? [resolved.expression] : [],
      filterInput: [filterInput],
      dateScope,
    })
      .then((response) => {
        if (!isActive) {
          return;
        }
        const first = response[0];
        setValue(first ? (first[metric.id] as number | undefined) : undefined);
        setIsLoading(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setValue(undefined);
        setIsError(true);
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [client, dateScope, filterInput, metric, resolved]);

  const formattedValue = useMemo(
    () => (metric ? formatMetric({ value, draftMetric: metric }) : '-'),
    [formatMetric, metric, value],
  );

  return { value, formattedValue, isLoading, isError };
};

export default useStudioMetricValue;
